import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useReducer } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

/** API row shape returned by /admin/compassion-mismatches */
interface Entry extends Record<PropertyKey, unknown> {
  id: string
  created_at: string
  birth_date: string | null
  email: string | null
  first_name: string | null
  last_name: string | null
  member_id: string | null
  alt_member_id: string | null
  group_number: string | null
  external_id: string | null
  updated_at: string
  resolved: boolean
  origin: string | null
}

type QueryKey = [
  'compassion-mismatches',
  SearchParams
]

interface QueryData {
  /** current page rows */
  rows?: Entry[] // if your backend returns { rows, total, totalPages }
  data?: Entry[] // kept for parity with your ref (Table.Table expects data)
  /** total items across all pages */
  total: number
  /** current page size */
  pageSize?: number
  /** current page index (0-based on the client in this UI) */
  page?: number
}

interface State {
  searchParams: SearchParams
}

type Action =
    | { type: 'query/set', payload: string }
    | { type: 'filters/set', payload: SearchParams['filters'] }
    | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
    | { type: 'filters/clear' }
    | { type: 'sort/set', payload: Sort }
    | { type: 'pagination/page/set', payload: number }
    | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  /** free-text query applied server-side with single ILIKE across concatenated fields */
  query: string
  sort: Sort
  pagination: Pagination
  filters: {
    resolved: ('resolved' | 'not-resolved')[]
  }
}

const sortKeys = [
  'created_at',
  'first_name',
  'last_name',
  'birth_date',
  'email',
  'member_id',
  'alt_member_id',
  'group_number',
  'external_id',
  'id',
  'resolved'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  /** 0-based page index for the UI (mirrors your reference) */
  page: number
  pageSize: number
}

const initialState: State = {
  searchParams: {
    query: '',
    sort: {
      field: 'created_at',
      direction: 'desc'
    },
    filters: {
      resolved: []
    },
    pagination: {
      page: 0,
      pageSize: 10
    }
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: { ...state.searchParams.pagination, page: 0 }
        }
      }
    }
    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {...state.searchParams.pagination, page: 0}
        }
      }
    }
    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {...state.searchParams.filters, ...action.payload},
          pagination: {...state.searchParams.pagination, page: 0}
        }
      }
    }
    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {...state.searchParams.pagination, page: 0}
        }
      }
    }
    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: { ...state.searchParams.pagination, page: 0 }
        }
      }
    }
    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: { ...state.searchParams.pagination, page: action.payload }
        }
      }
    }
    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: { pageSize: action.payload, page: 0 }
        }
      }
    }

    default:
      return state
  }
}

/** Optional mapping if UI column names differ from DB column names */
const sortMapper: Record<string, string> = {
  // e.g. name: 'last_name'
}

/** Keep the SearchInput clear button logic consistent with your ref */
function isDefaultSearchParams(sp: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
      sp.query === initialState.searchParams.query &&
      sp.filters.resolved.length === initialState.searchParams.filters.resolved.length &&
      sp.filters.resolved.every(v => initialState.searchParams.filters.resolved.includes(v))
  )
}

export function CompassionMismatchesPage (): JSX.Element {
  const [ state, dispatch ] = useReducer(reducer, initialState)
  const [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  const query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'compassion-mismatches',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],
    async queryFn ({ queryKey }) {
      const [ , sp ] = queryKey
      const {query, sort, pagination, filters} = sp
      const normalized = query.trim().toLowerCase()

      const params = new URLSearchParams([
        [ 'pageSize', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ], // 0-based like your reference
        [ 'sortBy', sortMapper[sort.field] ?? sort.field ],
        [ 'sortDir', sort.direction ],
        ...(normalized ? [['search', normalized]] : []),
        ...filters.resolved.map(v => ['filters.resolved', v]),
      ])

      // Backend handler should accept these params and return
      // { rows, total, page, pageSize } OR { data, total, count }
      const res = await http.getRequest(`/admin/compassion-mismatches?${params.toString()}`)

      // Normalize to the shape Table.Table expects (data + total)
      if (res?.rows && !res?.data) {
        return { ...res, data: res.rows }
      }
      return res
    },
    placeholderData: keepPreviousData
  })

  const columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'created_at',
      header: 'Created',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(entry.created_at)}
          </span>
        )
      }
    },
    {
      name: 'updated_at',
      header: 'Last Updated',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(entry.updated_at)}
          </span>
        )
      }
    },
    {
      name: 'first_name',
      header: 'First Name',
      isSortable: true
    },
    {
      name: 'last_name',
      header: 'Last Name',
      isSortable: true
    },
    {
      name: 'birth_date',
      header: 'Birth Date',
      isSortable: true,
      getValue (entry) {
        if (typeof entry.birth_date === 'string') {
          return entry.birth_date.split('T')[0]
        }
        return null
      }
    },
    {
      name: 'email',
      header: 'Email',
      isSortable: true
    },
    {
      name: 'phone',
      header: 'Phone',
      isSortable: true
    },
    {
      name: 'group_number',
      header: 'Group Number',
      isSortable: true
    },
    {
      name: 'member_id',
      header: 'Member ID',
      isSortable: true
    },
    {
      name: 'alt_member_id',
      header: 'Alt Member ID',
      isSortable: true
    },
    {
      name: 'dependent_number',
      header: 'Dependent Number',
      isSortable: true
    },
    {
      name: 'external_id',
      header: 'Cognito Sub',
      isSortable: true
    },
    {
      name: 'resolved',
      header: 'Resolved',
      getValue (entry) {
        if (entry.resolved) {
          return 'Resolved'
        }
        return null
      },
      isSortable: true
    },
    {
      name: 'origin',
      header: 'Origin',
      getValue (entry) {
        if (!entry.origin) return null
        if (entry.origin === 'narus') return 'mPower'
        if (entry.origin === 'member-portal') return 'Member Portal'
        return entry.origin
      }
    }
  ], [])

  const handleSortChange = useCallback((field: keyof Entry, direction: SortDirection) => {
    const isSortKey = (k: unknown): k is SortKey =>
      (sortKeys as readonly unknown[]).includes(k)
    if (!isSortKey(field)) return

    dispatch({ type: 'sort/set', payload: { field, direction } })
  }, [])

  const handlePageChange = useCallback((value: number) => {
    // Table emits 1-based, keep 0-based in state like your reference
    dispatch({ type: 'pagination/page/set', payload: value - 1 })
  }, [])

  const handlePageSizeChange = useCallback((value: number) => {
    dispatch({ type: 'pagination/page-size/set', payload: value })
  }, [])

  // filters/clear resets both query and filters to defaults (see reducer)
  const handleFiltersClear = useCallback(() => {
    dispatch({type: 'filters/clear'})
  }, [])

  const pagination = useMemo(() => {
    const total = query.data?.total ?? 0
    return {
      ...state.searchParams.pagination,
      totalFiltered: total,
      totalUnfiltered: total
    }
  }, [ state.searchParams.pagination, query.data ])

  let filters = useMemo(() => {
    return [
      {
        name: 'resolved',
        value: state.searchParams.filters.resolved,
        options: [
          {value: 'resolved', label: 'Resolved'},
          {value: 'not-resolved', label: 'Not Resolved'}
        ]
      },
    ]
  }, [state.searchParams.filters])

  let handleFilterChange = useCallback(<T extends keyof Entry>(name: T, value: Array<Entry[T]>): void => {
    dispatch({
      type: 'filters/merge',
      payload: {
        [name]: value
      }
    })
  }, [])


  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Compassion Matching</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by name, email, member/alt member/group/external id, and birth date'
          onChange={e => {
            dispatch({ type: 'query/set', payload: e.target.value })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No mismatches found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        onFilterChange={handleFilterChange}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}
